import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Chip, TableCell, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { Deletion, getDeletions } from 'src/api/oApi';
import { BrickwiseTable } from 'src/components/mui-overrides/BrickwiseTable';
import { LinkTableCell } from 'src/components/mui-overrides/TableCell';
import { Colors } from 'src/components/utils/Theme';
import { routes } from 'src/router/AppRouter';
import { generalStore } from 'src/stores/GeneralStore';

const rowsPerPage = 20;

type StatusFilter = 'running' | 'closed' | 'all';

const systemFlags: { key: keyof Deletion['systems']; label: string }[] = [
  { key: 'authDeleted', label: 'Auth' },
  { key: 'customerDeleted', label: 'Customer' },
  { key: 'dbDeleted', label: 'DB' },
  { key: 'mangopayClosed', label: 'Mangopay' },
  { key: 'mailjetDeleted', label: 'Mailjet' },
];

const formatDate = (date?: string) => {
  if (!date) return '';
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? date : parsed.toLocaleString();
};

const BooleanFlag: FC<{ ok: boolean; label?: string }> = ({ ok, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '8px', whiteSpace: 'nowrap' }}>
    {ok ? <CheckIcon fontSize="small" style={{ color: Colors.SUCCESS }} /> : <CloseIcon fontSize="small" style={{ color: Colors.ERROR }} />}
    {label ? <span style={{ fontSize: '11px' }}>{label}</span> : null}
  </span>
);

export const DeletionsSite = () => {
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<StatusFilter>('running');
  const [deletions, setDeletions] = useState<{ data: Deletion[]; count: number } | null>(null);

  useEffect(() => {
    const load = async () => {
      generalStore.setIsLoading(true);
      try {
        const response = await getDeletions({
          status: status === 'all' ? undefined : status,
          skip: page * rowsPerPage,
          limit: rowsPerPage,
        });
        setDeletions(response.data.data);
      } catch (error: any) {
        generalStore.setError(
          error?.response?.data?.error?.message || error?.response?.data?.error || error?.message || 'Error loading deletions',
        );
      } finally {
        generalStore.setIsLoading(false);
      }
    };
    load();
  }, [page, status]);

  return (
    <>
      <Typography variant="h5" style={{ fontFamily: 'Aestetico-semibold', color: Colors.BLACK_BLUE }} sx={{ mb: 2 }}>
        Account Deletions
      </Typography>
      <ToggleButtonGroup
        value={status}
        exclusive
        size="small"
        sx={{ mb: 2 }}
        onChange={(event, value: StatusFilter | null) => {
          if (value) {
            setStatus(value);
            setPage(0);
          }
        }}
      >
        <ToggleButton value="running">Running</ToggleButton>
        <ToggleButton value="closed">Closed</ToggleButton>
        <ToggleButton value="all">All</ToggleButton>
      </ToggleButtonGroup>
      <BrickwiseTable
        hidePagination={false}
        hideChangePageLengthChange={true}
        length={deletions?.count ?? 0}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        renderHeader={() => (
          <TableRow>
            <TableCell>UserId</TableCell>
            <TableCell>Email</TableCell>
            <TableCell style={{ textAlign: 'center' }}>State</TableCell>
            <TableCell style={{ textAlign: 'center' }}>Had Transactions</TableCell>
            <TableCell>Systems</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Updated At</TableCell>
          </TableRow>
        )}
        renderRows={() => (
          <>
            {deletions?.data.map((row, index) => (
              <TableRow key={row.userId + index} hover>
                <LinkTableCell to={routes.USERDETAIL.replace(':id', row.userId)}>{row.userId}</LinkTableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <Chip size="small" label={row.state} color={row.state === 'closed' ? 'success' : 'warning'} />
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <BooleanFlag ok={row.hadTransactions} />
                </TableCell>
                <TableCell>
                  {systemFlags.map((flag) => (
                    <Tooltip key={flag.key} title={`${flag.label}: ${row.systems?.[flag.key] ? 'done' : 'pending'}`}>
                      <span>
                        <BooleanFlag ok={!!row.systems?.[flag.key]} label={flag.label} />
                      </span>
                    </Tooltip>
                  ))}
                </TableCell>
                <TableCell>{formatDate(row.createdAt)}</TableCell>
                <TableCell>{formatDate(row.updatedAt)}</TableCell>
              </TableRow>
            ))}
          </>
        )}
      />
    </>
  );
};
